import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDown, ChevronDown, Settings2, Loader2, Info, RefreshCw, AlertTriangle,
} from "lucide-react";
import {
  useAccount, useSwitchChain, useSendTransaction, useWriteContract, usePublicClient,
} from "wagmi";
import { parseUnits, formatUnits, erc20Abi, maxUint256 } from "viem";
import { useWallet, useConnection } from "@/hooks/useSolCompat";
import { VersionedTransaction, Transaction as SolTransaction } from "@solana/web3.js";
import { useCurrentAccount, useDAppKit, useCurrentNetwork } from "@mysten/dapp-kit-react";
import { Transaction as SuiTransaction } from "@mysten/sui/transactions";
import { useTronWallet, signAndBroadcastTron } from "@/lib/TronProviders";
import { useBitcoinWallet, signAndBroadcastBitcoin } from "@/lib/BitcoinProviders";
import bs58 from "bs58";
import { toast } from "sonner";
import { SUPPORTED_CHAINS, NATIVE_ADDRESS } from "@/lib/wallet";
import { dexApi } from "@/lib/lifi";
import { saveTx } from "@/lib/history";
import { useTokenBalance } from "@/hooks/useTokenBalance";
import { useSolanaBalance } from "@/hooks/useSolanaBalance";
import TokenModal from "@/components/TokenModal";
import ChainModal from "@/components/ChainModal";
import WalletHub from "@/components/WalletHub";
import { SuiConnectButton } from "@/components/SuiConnectButton";

const EXPLORERS = {
  1: "https://etherscan.io/tx/", 10: "https://optimistic.etherscan.io/tx/",
  56: "https://bscscan.com/tx/", 100: "https://gnosisscan.io/tx/",
  137: "https://polygonscan.com/tx/", 8453: "https://basescan.org/tx/",
  42161: "https://arbiscan.io/tx/", 43114: "https://snowtrace.io/tx/",
  59144: "https://lineascan.build/tx/", 534352: "https://scrollscan.com/tx/",
  324: "https://explorer.zksync.io/tx/", 5000: "https://mantlescan.info/tx/",
  81457: "https://blastscan.io/tx/", 34443: "https://explorer.mode.network/tx/",
  1088: "https://andromeda-explorer.metis.io/tx/", 25: "https://cronoscan.com/tx/",
  42220: "https://celoscan.io/tx/", 1284: "https://moonscan.io/tx/",
  1285: "https://moonriver.moonscan.io/tx/", 250: "https://ftmscan.com/tx/",
  1329: "https://seistream.app/txs/", 146: "https://sonicscan.org/tx/",
  204: "https://opbnb.bscscan.com/tx/", 80094: "https://berascan.com/tx/",
  1313161554: "https://aurorascan.dev/tx/",
  1151111081099710: "https://solscan.io/tx/",
  9270000000000000: "https://suivision.xyz/txblock/",
  728126428: "https://tronscan.org/#/transaction/",
  20000000000001: "https://mempool.space/tx/",
};

function useTokens(chainId) {
  const [tokens, setTokens] = useState(null);
  useEffect(() => {
    let cancel = false;
    setTokens(null);
    if (!chainId) return;
    dexApi.tokens(String(chainId)).then((res) => {
      if (cancel) return;
      setTokens(res?.tokens?.[String(chainId)] || []);
    }).catch(() => !cancel && setTokens([]));
    return () => { cancel = true; };
  }, [chainId]);
  return tokens;
}

function fmtBalance(v, decimals) {
  if (v == null) return "—";
  const n = Number(formatUnits(BigInt(v), decimals));
  if (n === 0) return "0";
  if (n < 0.0001) return "<0.0001";
  if (n < 1) return n.toFixed(4);
  if (n < 1000) return n.toFixed(3);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function ChainPickerBtn({ chain, onClick, testId }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md bg-[var(--panel-2)] hover:bg-[var(--panel-2)] border border-[var(--border)] text-[11px] text-[var(--text-muted)] transition-colors" data-testid={testId}>
      <img src={chain.icon} alt="" className="w-3.5 h-3.5 rounded-full" onError={(e) => (e.currentTarget.style.display = "none")} />
      <span className="max-w-[100px] truncate">{chain.name}</span>
      <ChevronDown size={10} />
    </button>
  );
}

function TokenBtn({ token, onClick, testId }) {
  return (
    <button onClick={onClick} className="inline-flex items-center gap-2 h-10 pl-2 pr-3 rounded-lg bg-[var(--panel-3)] hover:bg-[var(--panel-2)] border border-[var(--border)] transition-colors flex-shrink-0" data-testid={testId}>
      {token ? (
        token.logoURI ? (
          <img src={token.logoURI} alt="" className="w-6 h-6 rounded-full" onError={(e) => (e.currentTarget.style.display = "none")} />
        ) : (
          <div className="w-6 h-6 rounded-full bg-[var(--panel-2)] text-[10px] flex items-center justify-center text-[var(--text)]">{token.symbol?.[0]}</div>
        )
      ) : (
        <div className="w-6 h-6 rounded-full bg-[var(--panel-2)] border border-dashed border-[var(--border)]" />
      )}
      <span className="text-[13px] font-semibold text-[var(--text)]">{token ? token.symbol : "Select"}</span>
      <ChevronDown size={13} className="text-[var(--text-muted)]" />
    </button>
  );
}

export default function SwapWidget({ onQuoteChange, onHistoryChange, selectedRouteIndex = 0, onFromTokenChange }) {
  const { address, isConnected, chainId: walletChainId } = useAccount();
  const { switchChainAsync } = useSwitchChain();
  const publicClient = usePublicClient();
  const { sendTransactionAsync } = useSendTransaction();
  const { writeContractAsync } = useWriteContract();

  const solWallet = useWallet();
  const solConn = useConnection();
  const solConnected = !!solWallet?.publicKey;
  const solAddress = solWallet?.publicKey?.toBase58();

  // Sui wallet state
  const suiAccount = useCurrentAccount();
  const suiNetwork = useCurrentNetwork();
  const suiDApp = useDAppKit();
  const suiConnected = !!suiAccount?.address;
  const suiAddress = suiAccount?.address;

  // Tron + Bitcoin wallet state
  const tron = useTronWallet();
  const btc = useBitcoinWallet();

  const [fromChain, setFromChain] = useState({ ...SUPPORTED_CHAINS[0], chainType: "EVM" }); // Ethereum
  const [toChain, setToChain] = useState({ ...SUPPORTED_CHAINS.find(c => c.key === "base"), chainType: "EVM" });
  const [fromToken, setFromToken] = useState(null);
  const [toToken, setToToken] = useState(null);
  const [amount, setAmount] = useState("");
  const [slippage, setSlippage] = useState(0.0075);
  const [manualRecipient, setManualRecipient] = useState("");

  const [chainModal, setChainModal] = useState(null);
  const [walletHubOpen, setWalletHubOpen] = useState(false);
  const [tokenModal, setTokenModal] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  const fromTokens = useTokens(fromChain?.id);
  const toTokens = useTokens(toChain?.id);

  const [fromMounted, setFromMounted] = useState(false);
  const [toMounted, setToMounted] = useState(false);
  useEffect(() => { if (fromMounted) setFromToken(null); setFromMounted(true); }, [fromChain?.id]); // eslint-disable-line
  useEffect(() => { if (toMounted) setToToken(null); setToMounted(true); }, [toChain?.id]); // eslint-disable-line

  useEffect(() => {
    if (fromTokens && fromTokens.length && !fromToken) {
      const native = fromTokens.find((t) => t.address?.toLowerCase() === NATIVE_ADDRESS) || fromTokens[0];
      setFromToken(native);
    }
  }, [fromTokens]); // eslint-disable-line
  useEffect(() => {
    if (toTokens && toTokens.length && !toToken) {
      const pref = toTokens.find((t) => t.symbol === "USDC") || toTokens[0];
      setToToken(pref);
    }
  }, [toTokens]); // eslint-disable-line
  useEffect(() => { onFromTokenChange && onFromTokenChange(fromToken); }, [fromToken, onFromTokenChange]);

  const isSolanaFrom = fromChain?.chainType === "SVM" || fromChain?.isSolana;
  const isSolanaTo = toChain?.chainType === "SVM" || toChain?.isSolana;
  const isSuiFrom = fromChain?.chainType === "MVM";
  const isSuiTo = toChain?.chainType === "MVM";
  const isTronFrom = fromChain?.chainType === "TVM";
  const isTronTo = toChain?.chainType === "TVM";
  const isBtcFrom = fromChain?.chainType === "UTXO";
  const isBtcTo = toChain?.chainType === "UTXO";
  const isEvmTo = !toChain?.chainType || toChain?.chainType === "EVM";
  const isNonSignableTo = false; // all chain types are now signable
  const isCrossChain = fromChain?.id !== toChain?.id;
  const activeConnected =
    isSolanaFrom ? solConnected
    : isSuiFrom ? suiConnected
    : isTronFrom ? tron.connected
    : isBtcFrom ? btc.connected
    : isConnected;
  const activeAddress =
    isSolanaFrom ? solAddress
    : isSuiFrom ? suiAddress
    : isTronFrom ? tron.address
    : isBtcFrom ? btc.address
    : address;

  // Auto recipient (from connected wallet for that chain type); manual otherwise.
  const autoRecipient =
    isEvmTo ? address
    : isSolanaTo ? solAddress
    : isSuiTo ? suiAddress
    : isTronTo ? tron.address
    : isBtcTo ? btc.address
    : null;
  const effectiveRecipient = autoRecipient || manualRecipient.trim() || null;
  const needsManualRecipient = activeConnected && !autoRecipient;

  // Chain-type check on manually pasted address (loose validation only).
  const isRecipientValid = useMemo(() => {
    if (autoRecipient) return true;
    if (!needsManualRecipient) return true;
    const a = (manualRecipient || "").trim();
    if (!a) return false;
    if (isEvmTo) return /^0x[a-fA-F0-9]{40}$/.test(a);
    if (isSolanaTo) return /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(a);
    if (isTronTo) return /^T[a-zA-Z0-9]{33}$/.test(a);
    if (isSuiTo) return /^0x[a-fA-F0-9]{64}$/.test(a);
    if (isBtcTo) return a.length >= 26 && a.length <= 90;
    return a.length > 6;
  }, [manualRecipient, autoRecipient, needsManualRecipient, isEvmTo, isSolanaTo, isTronTo, isSuiTo, isBtcTo]);

  const fromBal = useTokenBalance({ address, token: (isSolanaFrom || isSuiFrom || isTronFrom || isBtcFrom) ? null : fromToken, chainId: fromChain?.id });
  const toBal = useTokenBalance({ address, token: isEvmTo ? toToken : null, chainId: toChain?.id });
  const fromSolBal = useSolanaBalance({ connection: solConn?.connection, owner: solAddress, token: isSolanaFrom ? fromToken : null, enabled: isSolanaFrom && !!solConnected });
  const toSolBal = useSolanaBalance({ connection: solConn?.connection, owner: solAddress, token: isSolanaTo ? toToken : null, enabled: isSolanaTo && !!solConnected });

  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState(null);
  const [executing, setExecuting] = useState(false);
  const [routes, setRoutes] = useState([]);

  const canQuote = fromToken && toToken && amount && parseFloat(amount) > 0 && isRecipientValid;

  const fromAmountWei = useMemo(() => {
    if (!fromToken || !amount) return null;
    try { return parseUnits(amount, fromToken.decimals).toString(); } catch { return null; }
  }, [amount, fromToken]);

  const fetchQuote = useCallback(async () => {
    if (!canQuote || !fromAmountWei) return;
    setQuoteLoading(true); setQuoteError(null); setQuote(null); setRoutes([]);
    try {
      const body = {
        fromChain: fromChain.id,
        toChain: toChain.id,
        fromToken: fromToken.address,
        toToken: toToken.address,
        fromAmount: fromAmountWei,
        fromAddress: activeAddress || (isSolanaFrom
          ? "So11111111111111111111111111111111111111112"
          : isSuiFrom
          ? "0x0000000000000000000000000000000000000000000000000000000000000000"
          : isTronFrom
          ? "T9yD14Nj9j7xAB4dbGeiX9h8unkKHxuWwb"
          : isBtcFrom
          ? "bc1qxy2kgdygjrsqtzq2n0yrf2493p83kkfjhx0wlh"
          : "0x1111111111111111111111111111111111111111"),
        toAddress: effectiveRecipient || activeAddress,
        slippage,
      };
      const q = await dexApi.quote(body);
      setQuote(q);
      let routesList = [q];
      try {
        const r = await dexApi.routes(body);
        routesList = (r?.routes || [q]);
        setRoutes(routesList);
      } catch { setRoutes([q]); }
      onQuoteChange && onQuoteChange({ mode: isCrossChain ? "cross-chain" : "same-chain", raw: q, quote: q, routes: routesList, fromToken, toToken, fromChain, toChain });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Quote failed";
      setQuoteError(typeof msg === "string" ? msg : JSON.stringify(msg));
      onQuoteChange && onQuoteChange(null);
    } finally { setQuoteLoading(false); }
  }, [canQuote, fromChain, toChain, fromToken, toToken, fromAmountWei, activeAddress, address, solAddress, effectiveRecipient, isSolanaFrom, isSolanaTo, isCrossChain, slippage, onQuoteChange]);

  useEffect(() => {
    if (!canQuote) { setQuote(null); setQuoteError(null); onQuoteChange && onQuoteChange(null); return; }
    const t = setTimeout(fetchQuote, 550);
    return () => clearTimeout(t);
  }, [fetchQuote, canQuote]); // eslint-disable-line

  useEffect(() => { setManualRecipient(""); }, [toChain?.id, toChain?.chainType]);

  const flip = () => {
    setFromChain(toChain); setToChain(fromChain);
    setFromToken(toToken); setToToken(fromToken);
    setAmount(""); setQuote(null);
  };

  // Unified balance view — picks EVM or Solana source depending on the selected chain type.
  const effFromBal = isSolanaFrom ? fromSolBal : fromBal;
  const effToBal = isSolanaTo ? toSolBal : (isEvmTo ? toBal : { data: null, decimals: 18, isLoading: false });

  const setPct = (p) => {
    if (!effFromBal.data || !fromToken) return;
    const total = BigInt(effFromBal.data);
    let use = (total * BigInt(Math.round(p * 100))) / 100n;
    if (p === 1 && fromToken.address?.toLowerCase() === NATIVE_ADDRESS) {
      const buffer = total / 100n;
      use = total > buffer ? total - buffer : total;
    }
    setAmount(formatUnits(use, fromToken.decimals));
  };

  const buyAmount = quote?.estimate?.toAmount;
  const minBuyAmount = quote?.estimate?.toAmountMin;
  const toAmountDisplay = buyAmount && toToken
    ? formatUnits(BigInt(buyAmount), toToken.decimals) : "";

  const executeSwap = async () => {
    if (!quote) return;
    setExecuting(true);
    try {
      // Tron source path
      if (isTronFrom) {
        if (!tron.connected) { toast.error("Connect a Tron wallet"); return; }
        toast.info("Sign in TronLink…");
        const txid = await signAndBroadcastTron({ tronWeb: tron.tronWeb, transactionRequest: quote.transactionRequest });
        const entry = {
          hash: txid, fromChainId: fromChain.id, toChainId: toChain.id,
          fromSymbol: fromToken.symbol, toSymbol: toToken.symbol,
          fromAmount: amount, toAmount: toAmountDisplay,
          tool: quote.tool || "DEX", crossChain: isCrossChain, status: "PENDING",
        };
        onHistoryChange && onHistoryChange(saveTx(tron.address, entry));
        toast.success("Swap submitted");
        return;
      }
      // Bitcoin source path
      if (isBtcFrom) {
        if (!btc.connected) { toast.error("Connect a Bitcoin wallet"); return; }
        toast.info(`Sign in ${btc.provider === "unisat" ? "UniSat" : "Xverse"}…`);
        const txid = await signAndBroadcastBitcoin({
          provider: btc.provider, address: btc.address,
          transactionRequest: quote.transactionRequest,
        });
        const entry = {
          hash: txid, fromChainId: fromChain.id, toChainId: toChain.id,
          fromSymbol: fromToken.symbol, toSymbol: toToken.symbol,
          fromAmount: amount, toAmount: toAmountDisplay,
          tool: quote.tool || "DEX", crossChain: isCrossChain, status: "PENDING",
        };
        onHistoryChange && onHistoryChange(saveTx(btc.address, entry));
        toast.success("Swap submitted");
        return;
      }
      // Sui source path
      if (isSuiFrom) {
        if (!suiConnected) { toast.error("Connect a Sui wallet"); return; }
        const req = quote.transactionRequest;
        const raw = req?.data || req?.serializedTransaction;
        if (!raw) throw new Error("Quote missing Sui transaction");
        const tx = SuiTransaction.from(raw);
        tx.setSenderIfNotSet(suiAddress);
        toast.info("Sign in your Sui wallet…");
        const result = await suiDApp.signAndExecuteTransaction({
          transaction: tx,
          network: suiNetwork || "mainnet",
        });
        if (result?.$kind === "FailedTransaction") {
          throw new Error(result.FailedTransaction?.status?.error?.message || "Sui transaction failed");
        }
        const digest = result?.Transaction?.digest || result?.digest;
        const entry = {
          hash: digest, fromChainId: fromChain.id, toChainId: toChain.id,
          fromSymbol: fromToken.symbol, toSymbol: toToken.symbol,
          fromAmount: amount, toAmount: toAmountDisplay,
          tool: quote.tool || "DEX", crossChain: isCrossChain, status: "PENDING",
        };
        onHistoryChange && onHistoryChange(saveTx(suiAddress, entry));
        toast.success("Swap submitted");
        return;
      }

      // Solana source path
      if (isSolanaFrom) {
        if (!solConnected || !solWallet.signTransaction) { toast.error("Connect a Solana wallet"); return; }
        const req = quote.transactionRequest;
        const raw = req?.data || req?.serializedTransaction;
        if (!raw) throw new Error("Quote missing Solana transaction");
        const buf = typeof raw === "string" && raw.startsWith("0x")
          ? Buffer.from(raw.slice(2), "hex") : Buffer.from(bs58.decode(raw));
        let tx;
        try { tx = VersionedTransaction.deserialize(buf); } catch { tx = SolTransaction.from(buf); }
        toast.info("Sign in your Solana wallet…");
        const signed = await solWallet.signTransaction(tx);
        const sig = await solConn.connection.sendRawTransaction(signed.serialize(), { skipPreflight: false });
        const entry = {
          hash: sig, fromChainId: fromChain.id, toChainId: toChain.id,
          fromSymbol: fromToken.symbol, toSymbol: toToken.symbol,
          fromAmount: amount, toAmount: toAmountDisplay,
          tool: quote.tool || "DEX", crossChain: isCrossChain, status: "PENDING",
        };
        onHistoryChange && onHistoryChange(saveTx(solAddress, entry));
        toast.success("Swap submitted");
        return;
      }

      // EVM source path
      if (!address) return;
      if (walletChainId !== fromChain.id) await switchChainAsync({ chainId: fromChain.id });

      const isErc20 = fromToken.address.toLowerCase() !== NATIVE_ADDRESS;
      if (isErc20 && publicClient) {
        const spender = quote?.estimate?.approvalAddress || quote?.transactionRequest?.to;
        if (spender) {
          const allowance = await publicClient.readContract({
            address: fromToken.address, abi: erc20Abi, functionName: "allowance", args: [address, spender],
          });
          if (BigInt(allowance) < BigInt(fromAmountWei)) {
            toast.info("Approving token…");
            const approvalHash = await writeContractAsync({
              address: fromToken.address, abi: erc20Abi, functionName: "approve",
              args: [spender, maxUint256], chainId: fromChain.id,
            });
            await publicClient.waitForTransactionReceipt({ hash: approvalHash });
            toast.success("Approved");
          }
        }
      }

      const req = quote.transactionRequest;
      if (!req) throw new Error("Missing transactionRequest");
      toast.info("Confirm swap in wallet…");
      const hash = await sendTransactionAsync({
        to: req.to, data: req.data,
        value: req.value ? BigInt(req.value) : 0n,
        gas: req.gasLimit ? BigInt(req.gasLimit) : undefined,
        chainId: fromChain.id,
      });
      const entry = {
        hash, fromChainId: fromChain.id, toChainId: toChain.id,
        fromSymbol: fromToken.symbol, toSymbol: toToken.symbol,
        fromAmount: amount, toAmount: toAmountDisplay,
        tool: quote.tool || "DEX", crossChain: isCrossChain, status: "PENDING",
      };
      onHistoryChange && onHistoryChange(saveTx(address, entry));
      toast.success("Swap submitted");
    } catch (e) {
      toast.error(e?.shortMessage || e?.message || "Swap failed");
    } finally { setExecuting(false); }
  };

  const ctaLabel = !activeConnected
    ? (isSolanaFrom ? "Connect Solana wallet"
       : isSuiFrom ? "Connect Sui wallet"
       : isTronFrom ? "Connect Tron wallet"
       : isBtcFrom ? "Connect Bitcoin wallet"
       : "Connect wallet")
    : !fromToken || !toToken ? "Select token"
    : !amount ? "Enter amount"
    : needsManualRecipient && !isRecipientValid ? "Enter recipient address"
    : quoteLoading ? "Fetching best route…"
    : quoteError ? "No route available"
    : !quote ? "Enter amount"
    : executing ? "Swapping…"
    : `Swap ${fromToken.symbol} → ${toToken.symbol}`;

  const ctaDisabled = !activeConnected || !quote || quoteLoading || executing || !!quoteError;
  const priceImpact = quote?.estimate?.priceImpact ? (parseFloat(quote.estimate.priceImpact) * 100).toFixed(2) : null;
  const lowSlippageCrossChain = isCrossChain && slippage <= 0.001;

  return (
    <div className="panel p-0 overflow-hidden">
      <div className="flex items-center justify-between px-3 sm:px-4 h-12 border-b border-[var(--border)]">
        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
          <span className="text-[14px] font-semibold text-[var(--text)] tracking-tight">Swap</span>
          {isCrossChain && (
            <span className="text-[10px] uppercase tracking-[0.24em] font-semibold text-[var(--accent)]">
              Cross-chain
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => fetchQuote()} className="w-8 h-8 rounded-md text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--panel-2)] inline-flex items-center justify-center" data-testid="refresh-quote-button">
            <RefreshCw size={14} className={quoteLoading ? "animate-spin" : ""} />
          </button>
          <button onClick={() => setShowSettings((v) => !v)} className="w-8 h-8 rounded-md text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--panel-2)] inline-flex items-center justify-center" data-testid="slippage-setting-button">
            <Settings2 size={14} />
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="px-3 sm:px-4 py-3 border-b border-[var(--border)] bg-[var(--panel-2)]/40">
          <div className="text-[11px] text-[var(--text-muted)] mb-2 uppercase tracking-wider">Max slippage</div>
          <div className="flex flex-wrap gap-1.5">
            {[
              { v: 0.001, label: "0.10%" },
              { v: 0.005, label: "0.50%" },
              { v: 0.0075, label: "0.75%", rec: true },
              { v: 0.01, label: "1.0%" },
              { v: 0.03, label: "3.0%" },
            ].map((s) => (
              <button
                key={s.v}
                onClick={() => setSlippage(s.v)}
                className={`pill inline-flex items-center gap-1 ${slippage === s.v ? "pill-accent" : ""}`}
                data-testid={`slippage-preset-${s.v}`}
              >
                {s.label}
                {s.rec && (
                  <span className={`text-[9px] font-mono uppercase tracking-wider ${slippage === s.v ? "text-[var(--bg)]/70" : "text-[var(--accent)]"}`}>
                    · rec
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="text-[10.5px] text-[var(--text-subtle)] mt-2 font-mono">
            0.75% is recommended for cross-chain trades to avoid failed swaps.
          </div>
        </div>
      )}

      {/* FROM */}
      <div className="px-3 sm:px-4 pt-4 pb-3">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-[var(--text-subtle)] font-semibold">From</span>
          <ChainPickerBtn chain={fromChain} onClick={() => setChainModal("from")} testId="from-chain-button" />
        </div>
        <div className="flex items-center gap-2">
          <input type="text" inputMode="decimal" placeholder="0.00" value={amount}
            onChange={(e) => { const v = e.target.value.replace(/,/g, "."); if (/^\d*\.?\d*$/.test(v)) setAmount(v); }}
            className="bg-transparent outline-none flex-1 min-w-0 num-lg text-[var(--text)] placeholder:text-[var(--text-dim)]"
            data-testid="from-amount-input" />
          <TokenBtn token={fromToken} onClick={() => setTokenModal("from")} testId="from-token-select-button" />
        </div>
        <div className="mt-2 flex items-center justify-between gap-2 text-[11px]">
          <span className="text-[var(--text-subtle)] num-sm truncate">
            {fromToken?.priceUSD && amount ? `≈ $${(parseFloat(fromToken.priceUSD) * parseFloat(amount || 0)).toFixed(2)}` : "\u00a0"}
          </span>
          <div className="flex items-center gap-1 sm:gap-1.5 flex-shrink-0">
            <span className="num-sm text-[var(--text-muted)] hidden sm:inline" data-testid="from-balance">
              Bal: {effFromBal.data != null ? fmtBalance(effFromBal.data, effFromBal.decimals) : (activeConnected ? "…" : "—")}
            </span>
            {[["25%", 0.25], ["50%", 0.5], ["MAX", 1]].map(([lbl, p]) => (
              <button key={lbl} onClick={() => setPct(p)} disabled={!effFromBal.data}
                className="text-[10px] font-mono font-semibold uppercase px-1.5 py-0.5 rounded text-[var(--text-muted)] hover:text-[var(--accent)] hover:bg-[var(--accent-soft)] disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-[var(--text-muted)]"
                data-testid={`amount-pill-${lbl}`}>{lbl}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="relative h-0">
        <button onClick={flip} className="absolute left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-lg bg-[var(--bg)] border border-[var(--border-strong)] hover:border-[var(--border-accent)] text-[var(--text)] flex items-center justify-center transition-colors" data-testid="swap-flip-button">
          <ArrowDown size={14} />
        </button>
      </div>

      {/* TO */}
      <div className="px-3 sm:px-4 pt-5 pb-4 bg-[var(--panel-2)]/40 border-t border-[var(--border)]">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[10.5px] uppercase tracking-[0.22em] text-[var(--text-subtle)] font-semibold">To <span className="text-[var(--text-dim)] font-normal normal-case tracking-normal">· estimated</span></span>
          <ChainPickerBtn chain={toChain} onClick={() => setChainModal("to")} testId="to-chain-button" />
        </div>
        <div className="flex items-center gap-2">
          <input type="text" value={quoteLoading ? "…" : toAmountDisplay} readOnly placeholder="0.00"
            className="bg-transparent outline-none flex-1 min-w-0 num-lg text-[var(--text)] placeholder:text-[var(--text-dim)]"
            data-testid="to-amount-input" />
          <TokenBtn token={toToken} onClick={() => setTokenModal("to")} testId="to-token-select-button" />
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px]">
          <span className="num-sm text-[var(--text-subtle)] truncate">
            {quote?.estimate?.toAmountUSD ? `≈ $${parseFloat(quote.estimate.toAmountUSD).toFixed(2)}` : "\u00a0"}
          </span>
          <span className="num-sm text-[var(--text-muted)] hidden sm:inline" data-testid="to-balance">
            Bal: {effToBal.data != null ? fmtBalance(effToBal.data, effToBal.decimals) : (activeConnected && (isEvmTo || isSolanaTo) ? "…" : "—")}
          </span>
        </div>

        {needsManualRecipient && (
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--text-muted)]">
                Recipient · {isTronTo ? "Tron" : isSuiTo ? "Sui" : isBtcTo ? "Bitcoin" : isSolanaTo ? "Solana" : "EVM"} address
              </span>
              {manualRecipient && (
                <span className={`text-[10px] font-mono ${isRecipientValid ? "text-[var(--up)]" : "text-[var(--down)]"}`}>
                  {isRecipientValid ? "valid" : "invalid"}
                </span>
              )}
            </div>
            <input
              value={manualRecipient}
              onChange={(e) => setManualRecipient(e.target.value)}
              placeholder={
                isTronTo ? "TX…"
                : isSuiTo ? "0x…64 hex"
                : isBtcTo ? "bc1… / 1… / 3…"
                : isSolanaTo ? "Solana address"
                : "0x…"
              }
              className={`w-full bg-[var(--panel-3)] border rounded-md px-3 py-2 text-[12px] font-mono text-[var(--text)] placeholder:text-[var(--text-dim)] outline-none transition-colors ${
                manualRecipient && !isRecipientValid ? "border-[var(--down)]/60" : "border-[var(--border)] focus:border-[var(--border-accent)]"
              }`}
              data-testid="recipient-address-input"
              spellCheck={false}
            />
          </div>
        )}
      </div>

      {lowSlippageCrossChain && (
        <div className="mx-3 sm:mx-4 mb-2 rounded-md border border-[var(--down)]/40 bg-[var(--down)]/10 p-2.5 flex items-start gap-2" data-testid="low-slippage-warning">
          <AlertTriangle size={14} className="text-[var(--down)] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-[11.5px] text-[var(--text)] font-semibold leading-tight">Slippage too low for cross-chain</div>
            <div className="text-[10.5px] text-[var(--text-muted)] mt-0.5 leading-snug">
              0.10% will very likely fail — bridges take 30s–5min and prices move.{" "}
              <button
                onClick={() => setSlippage(0.0075)}
                className="text-[var(--accent)] hover:underline font-mono"
                data-testid="fix-slippage-button"
              >
                Switch to 0.75% →
              </button>
            </div>
          </div>
        </div>
      )}

      {quote && !quoteError && (
        <div className="px-3 sm:px-4 py-3 border-t border-[var(--border)] text-[12px] space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="text-[var(--text-muted)] inline-flex items-center gap-1.5"><Info size={11} /> Rate</span>
            <span className="num-sm text-[var(--text)] truncate">
              1 {fromToken?.symbol} ≈ {buyAmount && toToken && amount
                ? (Number(formatUnits(BigInt(buyAmount), toToken.decimals)) / Number(amount)).toFixed(6) : "—"} {toToken?.symbol}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--text-muted)]">Min received</span>
            <span className="num-sm text-[var(--text)]">
              {minBuyAmount && toToken ? `${Number(formatUnits(BigInt(minBuyAmount), toToken.decimals)).toFixed(6)} ${toToken.symbol}` : "—"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--text-muted)]">Route</span>
            <span className="num-sm text-[var(--text)]">{quote.tool || quote.toolDetails?.name || "Direct"}</span>
          </div>
          {priceImpact != null && (
            <div className="flex items-center justify-between">
              <span className="text-[var(--text-muted)]">Price impact</span>
              <span className={`num-sm ${parseFloat(priceImpact) > 3 ? "text-[var(--down)]" : parseFloat(priceImpact) > 1 ? "text-[var(--warn)]" : "text-[var(--up)]"}`}>
                {priceImpact}%
              </span>
            </div>
          )}
        </div>
      )}

      {quoteError && (
        <div className="mx-3 sm:mx-4 mb-3 rounded-md border border-[var(--down)]/30 bg-[var(--down)]/10 text-[var(--down)] text-[11px] p-2.5 font-mono break-all">
          {String(quoteError).slice(0, 240)}
        </div>
      )}

      <div className="p-3 sm:p-4 pt-1">
        {!activeConnected ? (
          <button onClick={() => setWalletHubOpen(true)} className="btn btn-primary w-full h-12 text-[14px]" data-testid="swap-connect-wallet-button">
            {ctaLabel}
          </button>
        ) : (
          <button onClick={executeSwap} disabled={ctaDisabled} className="btn btn-primary w-full h-12 text-[14px]" data-testid="swap-execute-button">
            {(quoteLoading || executing) && <Loader2 size={16} className="animate-spin" />}
            {ctaLabel}
          </button>
        )}
        <div className="mt-2.5 flex items-center justify-between text-[10px] sm:text-[10.5px] text-[var(--text-subtle)] font-mono uppercase tracking-wider">
          <span>Non-custodial</span>
          <span className="hidden sm:inline">·</span>
          <span>DEX · 45+ sources</span>
          <span className="hidden sm:inline">·</span>
          <span>0.5% fee</span>
        </div>
      </div>

      <ChainModal open={chainModal === "from"} onClose={() => setChainModal(null)} onSelect={setFromChain} side="source" />
      <ChainModal open={chainModal === "to"} onClose={() => setChainModal(null)} onSelect={setToChain} side="destination" />
      <TokenModal open={tokenModal === "from"} onClose={() => setTokenModal(null)} tokens={fromTokens} onSelect={setFromToken} chainName={fromChain?.name} />
      <TokenModal open={tokenModal === "to"} onClose={() => setTokenModal(null)} tokens={toTokens} onSelect={setToToken} chainName={toChain?.name} />
      <WalletHub open={walletHubOpen} onClose={() => setWalletHubOpen(false)} />
    </div>
  );
}

export { EXPLORERS };
